import { useEffect, useState } from 'react';
import websocketService from '../services/websocket';
import { useAuth } from '../context/AuthContext';
import { logInfo, logError } from '../utils/logger';

const useWebSocket = () => {
  const { isAuthenticated, user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [gyms, setGyms] = useState({});
  const [selectedGym, setSelectedGym] = useState(null);
  const [gymMonsters, setGymMonsters] = useState([]);
  const [gymEvents, setGymEvents] = useState([]);

  // Connect to WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      logInfo('Connecting to WebSocket...');
      websocketService.connect();
      
      websocketService.on('open', () => setConnected(true));
      websocketService.on('close', () => setConnected(false));
      
      return () => {
        websocketService.off('open', () => setConnected(true));
        websocketService.off('close', () => setConnected(false));
        websocketService.disconnect();
      };
    }
  }, [isAuthenticated, user]);

  // Set up listeners for gym updates
  useEffect(() => {
    const handleGymsUpdate = (data) => {
      if (!data.gyms) return;
      
      const updatedGyms = { ...gyms };
      data.gyms.forEach(gym => {
        updatedGyms[gym.id] = gym;
		
        // Update selected gym if it's the one being updated
        if (selectedGym && selectedGym.id === gym.id) {
          setSelectedGym(gym);
        }
      });
      
      setGyms(updatedGyms);
    };
    
    const handleGymUpdate = (data) => {
      if (!data.gym) return;
      
      setGyms(prev => ({
        ...prev,
        [data.gym.id]: data.gym
      }));
      
      // Update selected gym if it's the one being updated
      if (selectedGym && selectedGym.id === data.gym.id) {
        setSelectedGym(data.gym);
      }
    };
    
    const handleGymDetails = (data) => {
      if (!data.gym) return;
      
      setSelectedGym(data.gym);
      setGymMonsters(data.monsters || []);
      setGymEvents(data.events || []);
    };
    
    const handleError = (data) => {
      logError('WebSocket error:', data.error);
    };
    
    websocketService.on('gyms_update', handleGymsUpdate);
    websocketService.on('gym_update', handleGymUpdate);
    websocketService.on('gym_details', handleGymDetails);
    websocketService.on('error', handleError);
    
    return () => {
      websocketService.off('gyms_update', handleGymsUpdate);
      websocketService.off('gym_update', handleGymUpdate);
      websocketService.off('gym_details', handleGymDetails);
      websocketService.off('error', handleError);
    };
  }, [gyms, selectedGym]);

  const updateCanvas = (bounds) => {
    if (!websocketService.isConnected) return;
    
    const canvas = {
      min_latitude: bounds.getSouth(),
      max_latitude: bounds.getNorth(),
      min_longitude: bounds.getWest(),
      max_longitude: bounds.getEast()
    };
    
    websocketService.updateCanvas(canvas);
  };

  const getGymDetails = (gymId, timeSpan) => {
    if (!websocketService.isConnected) return;
    websocketService.requestGymDetails(gymId, timeSpan);
  };

  const updateBadgeLevel = (gymId, badgeLevel) => {
    if (!websocketService.isConnected) return;
    websocketService.updateBadgeLevel(gymId, badgeLevel);
    
    // Update local state immediately for better UX
    setGyms(prev => {
      const updatedGyms = { ...prev };
      if (updatedGyms[gymId]) {
        updatedGyms[gymId] = {
          ...updatedGyms[gymId],
          badge_level: badgeLevel
        };
      }
      return updatedGyms;
    });
    
    if (selectedGym && selectedGym.id === gymId) {
      setSelectedGym(prev => ({
        ...prev,
        badge_level: badgeLevel
      }));
    }
  };

  return {
    connected,
    gyms: Object.values(gyms),
    selectedGym,
    gymMonsters,
    gymEvents,
    updateCanvas,
    getGymDetails,
    updateBadgeLevel
  };
};

export default useWebSocket;